'use client';

import { c as _c } from "react/compiler-runtime";
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { ExternLogoFull } from '@extern/ui/logos';
import Link from 'next/link';
import { LabsSurfaceProvider } from '@/app/(platform)/(subscription-gated)/labs/_engine/labs-surface';
import { PackProvider } from '@/app/(platform)/(subscription-gated)/labs/_engine/pack-context';
import { CONSENT_DIALOG_HIDDEN_CSS, isConsentDialogSuppressed } from '@/lib/consent/embedded-consent-dialog';
import { getPreviewVisitorId } from '../_engine/anonymous-id';
import { isEmbedded, readPlacementFromLocation } from '../_engine/entry-placement';
import { readGuideSourceFromLocation } from '../_engine/guide-source';
import { PREVIEW_ENTRY, readOpeningAnswerFromLocation } from '../_engine/opening-answer';
import { PreviewAnalyticsProvider } from '../_engine/preview-analytics';
import { PreviewContainer } from '../_engine/preview-container';
import { toPreviewPack } from '../_engine/preview-copy';
import { PreviewFlow } from '../_engine/preview-flow';
import { resolvePreviewPack } from '../_engine/preview-packs';
import { PREVIEW_MODULE_COUNT, PREVIEW_STEPS, PREVIEW_STORE_VARIANT } from '../_engine/preview-steps';

/**
 * The public preview's client tree, and the one place its four providers are composed in order:
 *
 *   PackProvider              the vertical's content, plus a store bound to the SHORT step sequence
 *     LabsSurfaceProvider     two modules, no trial bar (the sprint's screens ask the host for both)
 *       PreviewContainer      seeds the `?a1=` opening answer, restores the draft, never gates render
 *         PreviewAnalyticsProvider   arm-free tracking, which needs the visitor id and entry path
 *           PreviewFlow
 *
 * The analytics provider sits INSIDE the container on purpose. `labs_preview_viewed` is the
 * denominator for the whole funnel, and firing it before the visitor id resolved would leave the
 * fastest-bouncing readers carrying a null id. Those are the readers the number is most about.
 */

/** Height of the header, matching `theme(spacing.16)`, which is what `StepShell` subtracts from the viewport. */
const HEADER_CLASS = 'flex h-16 shrink-0 items-center justify-between border-fs-border border-b bg-fs-surface px-4';

/**
 * Takes the vertical SLUG rather than a pack, because a pack cannot cross the server-to-client
 * boundary: it carries `deriveArchetype` and a Lucide icon, and React serializes neither. The
 * registry is a plain module, so resolving here reaches exactly the same pack.
 */
export function PreviewApp(t0) {
  const $ = _c(27);
  const {
    vertical
  } = t0;
  const [visitorId] = useState(getPreviewVisitorId);
  const [guideSource] = useState(readGuideSourceFromLocation);
  const [openingAnswer] = useState(readOpeningAnswerFromLocation);
  const [placement] = useState(readPlacementFromLocation);
  let t1;
  if ($[0] !== placement) {
    t1 = isEmbedded(placement);
    $[0] = placement;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const embedded = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  useEffect(_temp, t2);
  let t3;
  if ($[3] !== vertical) {
    t3 = resolvePreviewPack(vertical);
    $[3] = vertical;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const pack = t3;
  let t4;
  if ($[5] !== pack) {
    t4 = pack ? toPreviewPack(pack) : null;
    $[5] = pack;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const previewPack = t4;
  if (!previewPack) {
    return null;
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      steps: PREVIEW_STEPS,
      variant: PREVIEW_STORE_VARIANT
    };
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      moduleCount: PREVIEW_MODULE_COUNT,
      trialBar: false
    };
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== embedded) {
    t7 = embedded ? null : <header className={HEADER_CLASS}><Link aria-label="Extern" href="https://extern.com"><ExternLogoFull className="h-6 w-auto" /></Link><Link className="rounded-fs-pill px-4 py-2 font-medium text-fs-body-md text-fs-brand hover:bg-fs-brand-subtle" href="/sign-in">Sign in</Link></header>;
    $[9] = embedded;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const t8 = openingAnswer ? PREVIEW_ENTRY.QUESTION : PREVIEW_ENTRY.LINK;
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <PreviewFlow />;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== guideSource || $[13] !== placement || $[14] !== t8 || $[15] !== visitorId) {
    t10 = <PreviewAnalyticsProvider entry={t8} guideSource={guideSource} placement={placement} visitorId={visitorId}>{t9}</PreviewAnalyticsProvider>;
    $[12] = guideSource;
    $[13] = placement;
    $[14] = t8;
    $[15] = visitorId;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== embedded || $[18] !== openingAnswer || $[19] !== t10) {
    t11 = <PreviewContainer openingAnswer={openingAnswer} startAtDeck={embedded}>{t10}</PreviewContainer>;
    $[17] = embedded;
    $[18] = openingAnswer;
    $[19] = t10;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t11 || $[22] !== t7) {
    t12 = <LabsSurfaceProvider surface={t6}><div className="flex min-h-dvh flex-col bg-fs-canvas">{t7}{t11}</div></LabsSurfaceProvider>;
    $[21] = t11;
    $[22] = t7;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== previewPack || $[25] !== t12) {
    t13 = <PackProvider pack={previewPack} storeOptions={t5}>{t12}</PackProvider>;
    $[24] = previewPack;
    $[25] = t12;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  return t13;
}
function _temp() {
  if (!isConsentDialogSuppressed()) {
    return;
  }
  const style = document.createElement("style");
  style.setAttribute("data-consent-dialog", "hidden-in-frame");
  style.textContent = CONSENT_DIALOG_HIDDEN_CSS;
  document.head.appendChild(style);
  return () => style.remove();
}